import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import { 
  Layout, ShieldCheck, Mail, Star, ChevronRight, 
  Menu, X, BookOpen, Clock, Tag, ArrowRight
} from 'lucide-react';

const ICON_MAP = {
  Layout: Layout,
  ShieldCheck: ShieldCheck,
  Mail: Mail,
};

const IS_COMING_SOON = true;

export default function Documentation() {
  const [navigation, setNavigation] = useState([]);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();

  const activeModuleId = searchParams.get('mod') || 'pilotage';
  const activeChapterId = searchParams.get('chap') || 'dashboard';

  if (IS_COMING_SOON) {
    return (
      <div className="min-h-screen bg-[#F5F3EE] font-body text-[#24574E] flex flex-col items-center justify-center p-6 text-center">
        <div className="max-w-2xl w-full py-20 animate-in fade-in slide-in-from-bottom-4 duration-1000">
          <div className="flex justify-center mb-12">
            <div className="w-24 h-24 bg-[#F9D965] rounded-[2rem] flex items-center justify-center text-[#24574E] shadow-2xl rotate-3">
              < BookOpen size={48} />
            </div>
          </div>

          <h1 className="text-5xl md:text-7xl font-black mb-8 leading-tight tracking-tight">
            La Documentation <br />
            <span className="italic text-[#5BC0AF]">arrive bientôt.</span>
          </h1>

          <p className="text-xl text-[#24574E]/60 mb-12 leading-relaxed font-medium max-w-lg mx-auto">
            Nous peaufinons les derniers guides pour vous aider à tirer le meilleur parti de SuperAsso. 
            Revenez dans quelques jours !
          </p>

          <div className="flex flex-col items-center gap-8">
            <Link 
              to="/"
              className="bg-[#24574E] text-white px-10 py-5 rounded-2xl font-black text-lg flex items-center gap-3 hover:scale-[1.05] active:scale-[0.98] transition-all shadow-[0_20px_50px_rgba(36,87,78,0.2)]"
            >
              Retour à l'accueil
              <ArrowRight size={22} />
            </Link>
            
            <div className="flex items-center gap-3 text-xs font-black text-[#24574E]/30 uppercase tracking-[0.3em]">
              <div className="w-2 h-2 rounded-full bg-[#5BC0AF] animate-pulse" />
              Rédaction en cours
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fetch navigation
  useEffect(() => {
    fetch('/docs/navigation.json')
      .then(res => res.json())
      .then(data => {
        setNavigation(data);
        setLoading(false);
      })
      .catch(err => console.error('Error loading navigation:', err));
  }, []);

  // Fetch content
  useEffect(() => {
    if (navigation.length === 0) return;

    const module = navigation.find(m => m.id === activeModuleId);
    if (!module) return;

    const chapter = module.chapters.find(c => c.id === activeChapterId);
    if (!chapter) return;

    setLoading(true);
    fetch(`/docs/content/${chapter.file}`)
      .then(res => {
        if (!res.ok) throw new Error('Fichier non trouvé');
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('text/html')) {
          throw new Error('Contenu invalide (HTML au lieu de Markdown)');
        }
        return res.text();
      })
      .then(text => {
        setContent(text);
        setLoading(false);
        window.scrollTo(0, 0);
      })
      .catch(err => {
        console.error('Error loading content:', err);
        setContent(`# ⚠️ Document non disponible\n\nLe contenu de ce chapitre est en cours de rédaction ou a été déplacé.\n\n[Retour à l'accueil](/docs)`);
        setLoading(false);
      });
  }, [navigation, activeModuleId, activeChapterId]);

  const handleSelectChapter = (modId, chapId) => {
    setSearchParams({ mod: modId, chap: chapId });
    setIsSidebarOpen(false);
  };

  return (
    <div className="bg-[#F5F3EE] font-body text-[#24574E] selection:bg-[#F9D965]/20">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-20 flex min-h-screen">
        {/* Sidebar Container */}
        <aside 
          className={`
            fixed lg:sticky top-20 z-40 w-80 h-[calc(100vh-5rem)] 
            bg-[#F5F3EE] border-r border-[#24574E]/5 overflow-y-auto
            transition-transform duration-300 ease-in-out
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
        >
          <div className="p-8 space-y-10">
            {navigation.map((module) => {
              const Icon = ICON_MAP[module.icon] || BookOpen;
              return (
                <div key={module.id} className="space-y-4">
                  <div className="flex items-center gap-3 px-2">
                    <div className="w-8 h-8 rounded-lg bg-[#24574E]/3 border border-[#24574E]/10 flex items-center justify-center">
                      <Icon size={16} className="text-[#24574E]/60" />
                    </div>
                    <span className="text-xs font-black uppercase tracking-widest text-[#24574E]/40">
                      {module.title}
                    </span>
                  </div>

                  <nav className="space-y-1">
                    {module.chapters.map((chapter) => {
                      const isActive = activeModuleId === module.id && activeChapterId === chapter.id;
                      return (
                        <button
                          key={chapter.id}
                          onClick={() => handleSelectChapter(module.id, chapter.id)}
                          className={`
                            w-full text-left px-4 py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-between group
                            ${isActive 
                              ? 'bg-[#24574E] text-white shadow-lg shadow-[#24574E]/10' 
                              : 'text-[#24574E]/60 hover:bg-[#24574E]/5 hover:text-[#24574E]'}
                          `}
                        >
                          {chapter.title}
                          <ChevronRight 
                            size={14} 
                            className={`transition-transform ${isActive ? 'translate-x-1' : 'opacity-0 group-hover:opacity-100'}`} 
                          />
                        </button>
                      );
                    })}
                  </nav>
                </div>
              );
            })}

            {/* Help Card */}
            <div className="mt-12 p-6 bg-[#F9D965]/10 border border-[#F9D965]/20 rounded-2xl relative overflow-hidden group">
              <div className="absolute -top-6 -right-6 w-20 h-20 bg-[#F9D965]/20 rounded-full blur-2xl transition-transform group-hover:scale-150" />
              <div className="relative">
                <h4 className="text-sm font-black mb-2">Besoin d'aide ?</h4>
                <p className="text-xs text-[#24574E]/60 font-medium leading-relaxed mb-4">
                  Notre équipe est là pour vous accompagner dans vos premiers pas.
                </p>
                <a 
                  href="mailto:support@superasso.fr"
                  className="text-xs font-black text-[#24574E] border-b-2 border-[#F9D965] hover:border-[#24574E] transition-all"
                >
                  Contactez le support
                </a>
              </div>
            </div>
          </div>
        </aside>

        {/* Backdrop for mobile */}
        {isSidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-30 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 px-6 py-12 lg:px-20 lg:py-16 max-w-5xl mx-auto w-full">
          {loading ? (
            <div className="flex items-center gap-3 text-[#24574E]/40 font-bold animate-pulse">
              <Clock size={20} className="animate-spin" />
              Chargement de la documentation...
            </div>
          ) : (
            <article className="prose prose-slate [&_h1]:text-[#24574E] [&_h1]:text-4xl [&_h1]:md:text-5xl [&_h1]:font-serif [&_h1]:mt-12 [&_h1]:mb-8 [&_h2]:text-[#24574E] [&_h2]:text-2xl [&_h2]:md:text-3xl [&_h2]:mt-12 [&_h2]:mb-6 [&_h3]:text-[#24574E] [&_h3]:text-xl [&_h3]:mt-8 [&_h3]:mb-4 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:pl-3 [&_li]:mb-2 [&_li]:text-[#24574E] prose-headings:font-black prose-a:text-[#24574E] prose-a:font-bold prose-strong:text-[#24574E] prose-img:rounded-3xl prose-img:shadow-2xl [&_img]:my-12 max-w-none text-[#24574E] [&_p]:mb-6">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm, remarkBreaks]}
                rehypePlugins={[rehypeRaw]}
              >
                {content}
              </ReactMarkdown>

              {/* Footer Doc */}
              <div className="mt-20 pt-10 border-t border-[#24574E]/5 flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4 text-[#24574E]/40">
                  <div className="flex items-center gap-1.5 bg-[#24574E]/5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">
                    <Tag size={10} /> Mis à jour récemment
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xs font-bold text-[#24574E]/40">Cet article vous a aidé ?</span>
                  <div className="flex items-center gap-2">
                    <button className="w-10 h-10 rounded-full border border-[#24574E]/10 flex items-center justify-center hover:bg-white hover:shadow-lg transition-all text-lg">👍</button>
                    <button className="w-10 h-10 rounded-full border border-[#24574E]/10 flex items-center justify-center hover:bg-white hover:shadow-lg transition-all text-lg">👎</button>
                  </div>
                </div>
              </div>
            </article>
          )}
        </main>
      </div>
    </div>
  );
}
